/**
 * A página do documento com a área a recortar (E214.F1, GUS-314).
 *
 * O desenho é o do recorte da assinatura na grafotécnica: o mesmo `DocumentPageCanvas`, em modo de
 * desenho até a área existir e com a caixa ajustável pelos cantos depois. A área sai em frações da
 * página (`bbox_coords`), a convenção que o servidor recorta. A página vem da rota que confere a
 * custódia (`lerImagemDaPagina`); o endereço local é revogado ao trocar de página.
 */
import type { AxiosInstance } from 'axios';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useState } from 'react';

import { DocumentPageCanvas } from '@/components/CaseWizard/DocumentPageCanvas';
import { Button, FalhaComSaida } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { NormalizedBoundingBox } from '@/lib/bbox_coords';
import { lerImagemDaPagina } from '@/lib/ficha_da_leitura';

const t = ptBR.recorteDoDocumento;

type ImagemDaPagina =
  | { estado: 'carregando' }
  | { estado: 'pronta'; url: string }
  | { estado: 'erro' };

function useImagemDaPagina(
  client: AxiosInstance,
  documentoId: string,
  pagina: number,
  tentativa: number,
): ImagemDaPagina {
  const [imagem, setImagem] = useState<ImagemDaPagina>({ estado: 'carregando' });
  useEffect(() => {
    let vivo = true;
    let url: string | null = null;
    setImagem({ estado: 'carregando' });
    lerImagemDaPagina(client, documentoId, pagina)
      .then((lida) => {
        url = lida;
        if (vivo) setImagem({ estado: 'pronta', url: lida });
        else URL.revokeObjectURL(lida);
      })
      .catch(() => {
        if (vivo) setImagem({ estado: 'erro' });
      });
    return () => {
      vivo = false;
      if (url !== null) URL.revokeObjectURL(url);
    };
  }, [client, documentoId, pagina, tentativa]);
  return imagem;
}

export interface PaginaDoRecorteProps {
  client: AxiosInstance;
  documentoId: string;
  pagina: number;
  total: number | null;
  area: NormalizedBoundingBox | null;
  onPagina: (pagina: number) => void;
  onArea: (area: NormalizedBoundingBox | null) => void;
}

export function PaginaDoRecorte({
  client,
  documentoId,
  pagina,
  total,
  area,
  onPagina,
  onArea,
}: PaginaDoRecorteProps): JSX.Element {
  const [tentativa, setTentativa] = useState(0);
  const imagem = useImagemDaPagina(client, documentoId, pagina, tentativa);
  const rotulo =
    total === null
      ? t.paginaSemTotal.replace('{n}', String(pagina))
      : t.pagina.replace('{n}', String(pagina)).replace('{total}', String(total));
  return (
    <div className="space-y-sc-2">
      <div className="flex items-center gap-sc-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t.anterior}
          disabled={pagina <= 1}
          onClick={() => onPagina(pagina - 1)}
          data-testid="recorte-pagina-anterior"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden />
        </Button>
        <span className="text-body-sm text-fg-muted" data-testid="recorte-pagina">
          {rotulo}
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t.proxima}
          disabled={total !== null && pagina >= total}
          onClick={() => onPagina(pagina + 1)}
          data-testid="recorte-pagina-proxima"
        >
          <ChevronRight className="h-4 w-4" aria-hidden />
        </Button>
      </div>
      {imagem.estado === 'carregando' ? (
        <p className="text-body-sm text-fg-muted">{t.carregandoPagina}</p>
      ) : null}
      {imagem.estado === 'erro' ? (
        <FalhaComSaida
          mensagem={t.erroDaPagina}
          onTentarDeNovo={() => setTentativa((n) => n + 1)}
          data-testid="recorte-pagina-falha"
        />
      ) : null}
      {imagem.estado === 'pronta' ? (
        <div className="max-h-[60vh] overflow-auto border border-border bg-bg">
          <DocumentPageCanvas
            imageUrl={imagem.url}
            pageNumber={pagina}
            bboxes={
              area === null ? [] : [{ id: 'recorte', bbox: area, state: 'manual', label: '' }]
            }
            drawMode={area === null}
            maxWidthPx={640}
            maxHeightPx={560}
            onManualPinDrawn={(pin) => onArea(pin.bbox)}
            onBoundingBoxChange={(edit) => onArea(edit.bbox)}
          />
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-sc-2">
        <p className="text-caption text-fg-muted" data-testid="recorte-dica">
          {area === null ? t.desenhe : t.areaMarcada}
        </p>
        {area !== null ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onArea(null)}
            data-testid="recorte-de-novo"
          >
            {t.desenharDeNovo}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
